import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../ui/sheet';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { useCart } from '../../context/CartContext';
import { useRegional } from '../../context/RegionalContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartDrawer = () => {
  const { cart, isOpen, closeCart, updateQuantity, removeFromCart, loading } = useCart();
  const { region, getProductPrice } = useRegional();
  const navigate = useNavigate();
  
  // Regional prices for cart items
  const [regionalPrices, setRegionalPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  
  // Fetch regional prices when cart changes
  useEffect(() => {
    const fetchPrices = async () => {
      if (!cart.items || cart.items.length === 0) {
        setRegionalPrices({});
        return;
      }
      
      setLoadingPrices(true);
      const prices = {};
      
      for (const item of cart.items) {
        const product = item.product;
        const productId = item.product_id || product?.id;
        
        if (productId && product?.price) {
          try {
            const priceInfo = await getProductPrice(productId, product.price);
            if (priceInfo) {
              prices[productId] = priceInfo;
            }
          } catch (error) {
            console.warn('Failed to get regional price:', error);
          }
        }
      }
      
      setRegionalPrices(prices);
      setLoadingPrices(false);
    };
    
    if (isOpen) {
      fetchPrices();
    }
  }, [cart.items, isOpen, getProductPrice, region.code]);

  const handleCheckout = () => {
    closeCart();
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    closeCart();
    navigate('/products');
  };

  // Helper to get item details for both guest and authenticated carts
  const getItemProduct = (item) => {
    return item.product;
  };

  // Get item total with regional pricing
  const getItemTotal = (item) => {
    const product = getItemProduct(item);
    const productId = item.product_id || product?.id;
    const regionalPrice = regionalPrices[productId];
    
    if (regionalPrice) {
      return regionalPrice.price * item.quantity;
    }
    
    // Fallback to USD
    if (item.item_total !== undefined) {
      return item.item_total;
    }
    return (product?.price || 0) * item.quantity;
  };
  
  // Get unit price with regional pricing
  const getUnitPrice = (item) => {
    const product = getItemProduct(item);
    const productId = item.product_id || product?.id;
    const regionalPrice = regionalPrices[productId];
    
    if (regionalPrice) {
      return regionalPrice.price;
    }
    return product?.price || 0;
  };

  const getProductId = (item) => {
    return item.product_id || item.product?.id;
  };
  
  // Calculate cart totals with regional pricing
  const calculateCartTotals = () => {
    let subtotal = 0;
    
    for (const item of cart.items) {
      const productId = getProductId(item);
      const regionalPrice = regionalPrices[productId];
      
      if (regionalPrice) {
        subtotal += regionalPrice.price * item.quantity;
      } else {
        const product = getItemProduct(item);
        subtotal += (product?.price || 0) * item.quantity;
      }
    }
    
    return {
      subtotal,
      discount: cart.discount || 0,
      total: subtotal - (cart.discount || 0)
    };
  };
  
  const totals = calculateCartTotals();
  const currencySymbol = region.symbol || '$';

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-xl flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Your Cart
            {cart.items.length > 0 && (
              <span className="text-sm font-normal text-slate-500">
                ({cart.items.length} {cart.items.length === 1 ? 'item' : 'items'})
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {cart.items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">Your cart is empty</h3>
            <p className="text-sm text-slate-500 mb-6">Add some research amino acid derivatives to get started.</p>
            <Button onClick={handleContinueShopping} className="bg-slate-900 hover:bg-slate-800">
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {cart.items.map((item, index) => {
                const product = getItemProduct(item);
                const itemTotal = getItemTotal(item);
                const productId = getProductId(item);
                
                if (!product) return null;
                
                return (
                  <div key={productId || index} className="flex gap-4 p-3 bg-slate-50 rounded-xl" data-testid={`cart-item-${productId}`}>
                    <div className="w-20 h-20 bg-gradient-to-br from-white via-slate-50 to-white rounded-lg overflow-hidden flex-shrink-0 border border-slate-100">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-contain p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading font-semibold text-slate-900 text-sm truncate">
                        {product.name}
                      </h4>
                      <p className="text-xs text-slate-500 mb-2">{product.dosage}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(productId, item.quantity - 1)}
                            disabled={loading || item.quantity <= 1}
                            className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:border-slate-300 transition-colors disabled:opacity-50"
                            data-testid={`cart-decrease-${productId}`}
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(productId, item.quantity + 1)}
                            disabled={loading}
                            className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:border-slate-300 transition-colors disabled:opacity-50"
                            data-testid={`cart-increase-${productId}`}
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeFromCart(productId)}
                          disabled={loading}
                          className="p-1.5 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                          data-testid={`cart-remove-${productId}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-heading font-semibold text-slate-900">
                        {currencySymbol}{getItemTotal(item).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {currencySymbol}{getUnitPrice(item).toFixed(2)} each
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Cart Summary */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium">{currencySymbol}{totals.subtotal.toFixed(2)}</span>
              </div>
              {totals.discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span>-{currencySymbol}{totals.discount.toFixed(2)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between">
                <span className="font-heading font-semibold text-lg">Total</span>
                <span className="font-heading font-bold text-xl">{currencySymbol}{totals.total.toFixed(2)}</span>
              </div>
              {region.code !== 'US' && (
                <p className="text-xs text-slate-500 text-center">
                  Prices shown in {region.currency}
                </p>
              )}
            </div>

            <SheetFooter className="mt-4 flex-col gap-2">
              <Button
                onClick={handleCheckout}
                className="w-full bg-slate-900 hover:bg-slate-800 h-12 rounded-full text-base"
                disabled={loading}
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </Button>
              <Button
                variant="outline"
                onClick={handleContinueShopping}
                className="w-full h-10 rounded-full"
              >
                Continue Shopping
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
